// Phase 7 Task 13: a small, deliberately NOT-general currency-label lookup — same "fixed,
// deterministic per-currency mapping, not data-driven" precedent
// `admin/EconomyDashboardScreen.tsx`'s own `CURRENCY_COLORS` already established for this
// codebase's exactly-three-currencies-that-have-ever-shipped reality (shells, motes,
// lantern_sparks). A currency this map doesn't know about falls back to a humanized version of
// its own code (`lantern_sparks` -> `Lantern Sparks`) rather than the raw snake_case string,
// so a future currency never renders as illegible code in player-facing copy.
const CURRENCY_LABELS: Record<string, string> = {
  shells: 'Shells',
  motes: 'Motes',
  lantern_sparks: 'Lantern Sparks',
};

/** "1 Shell", "25 Shells" — the one place a Shell count picks its number (QA 2026-09-23 F12). */
export function shellsLabel(amount: number): string {
  return amount === 1 ? 'Shell' : 'Shells';
}

export function currencyLabel(code: string): string {
  return CURRENCY_LABELS[code] ?? code.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// The two currencies `eventCurrencyCode` below must never mistake for a liveops event's own
// currency (Shells: the primary economy currency since Phase 2; Motes: retired by "Hide the
// empty" #14.4/2026-09-27 -- nothing grants one any more, but a wallet may still hold a Mote
// balance from before that change (#14.4: balances stay in the ledger, untouched), and
// `WalletDto` always carries the key at 0 even for a brand-new account since
// `LedgerService.balances` zero-fills every registered currency -- so it has to stay excluded
// here or it would win `eventCurrencyCode`'s "find" over the real event currency on every
// account). Anything else `WalletDto` carries is, in every currency this codebase has ever
// shipped, a liveops event's own currency (today, only ever `lantern_sparks`).
const BASE_CURRENCIES: readonly string[] = ['shells', 'motes'];

/**
 * The wallet's own currency key that isn't one of the two always-on currencies above — i.e. a
 * liveops event currency. Deliberately NOT cross-checked against any SPECIFIC active event's own
 * currency code: `GET /events/active` never exposes that (`EventActiveDto`'s own contract is
 * intentionally narrow — code/name/endsAt/land only, no bundle internals, `api/events.ts`'s own
 * header comment), so this is "any non-base wallet currency" — correct for every currency this
 * codebase has ever shipped (exactly one event currency exists in shipped content at a time). A
 * future build running two differently-currencied events at once would only ever surface ONE of
 * them here — an accepted, documented simplification per the task's own "minimal, not a general
 * N-currency HUD framework" instruction, not an oversight.
 */
export function eventCurrencyCode(wallet: Record<string, number> | null | undefined): string | null {
  if (!wallet) return null;
  return Object.keys(wallet).find((code) => !BASE_CURRENCIES.includes(code)) ?? null;
}
